/**
 * IntentPopover — change the launch goal IN PLACE (Plan D follow-up F2).
 *
 * The header's `change` affordance used to be a link to the hosted questionnaire
 * funnel, which threw the user out of the app to answer four screens again. This
 * Popover edits the whole intent where it is displayed: goal, ad channels and
 * product analytics all feed `deriveRequirements`, so editing only the goal would
 * silently keep a stale channel set driving the pixel rows.
 *
 * Popover — never a Dialog (ruling F2): this is an inline edit of one header line,
 * not a task that deserves a modal. One primary action (`Apply`); the questionnaire
 * survives as a secondary text link that opens in a NEW TAB.
 *
 * Router-free (DC-9) and host-free: the caller owns the anchor and supplies
 * `onApply` (the page passes `useLaunchActivation().setIntent`, which replaces the
 * projection with the server's answer — the header and the list re-render from it).
 * Copy comes from `copy/intentCopy.ts` (AM-4/AM-14, English); theme tokens only.
 */
import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ActivationIntent, IntentChannel, IntentGoal, IntentProductAnalytics } from '@modules/shared-types';
import { INTENT_CHANNELS, INTENT_GOALS, INTENT_PRODUCT_ANALYTICS } from '@modules/shared-types';
import { LAUNCH_ACTIVATION_TEST_IDS as T } from '../testIds';
import { CHANNEL_LABELS, GOAL_LABELS, INTENT_POPOVER_COPY, PRODUCT_ANALYTICS_LABELS, capitalize } from '../copy/intentCopy';
import { CHIP_FOCUS_SX } from './chipFocusSx';

export interface IntentPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  /** The intent currently shown in the header — the popover opens preselected with it. */
  intent: ActivationIntent;
  /** Absolute questionnaire URL (already carrying `sourceProjectId`); the link is hidden when unset. */
  questionnaireHref?: string;
  onApply: (intent: Omit<ActivationIntent, 'updatedAt'>) => Promise<void>;
}

const POPOVER_WIDTH = 320;

function toggle<T>(values: readonly T[], value: T): T[] {
  return values.includes(value) ? values.filter((v) => v !== value) : [...values, value];
}

/**
 * Body of the popover. Split out so its draft state is created on OPEN and dropped on
 * close: MUI's Popover does not render its children while closed (no `keepMounted`
 * here), so mounting is the reset — an abandoned edit never leaks into the next open.
 */
const IntentPopoverBody: React.FC<Omit<IntentPopoverProps, 'anchorEl' | 'open'>> = ({ onClose, intent, questionnaireHref, onApply }) => {
  const [goal, setGoal] = useState<IntentGoal>(intent.goal);
  const [channels, setChannels] = useState<IntentChannel[]>(intent.channels);
  const [productAnalytics, setProductAnalytics] = useState<IntentProductAnalytics[]>(intent.productAnalytics);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const apply = async (): Promise<void> => {
    setSaving(true);
    setError(undefined);
    try {
      await onApply({
        goal,
        channels,
        productAnalytics,
        // Platform is profiling-only and this popover does not ask for it — carry the
        // stored value through so applying here never silently clears it.
        ...(intent.platform ? { platform: intent.platform } : {}),
        source: 'app',
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack data-testid={T.intentPopover} spacing={2} sx={{ p: 2, width: POPOVER_WIDTH }}>
      <FormControl size="small" fullWidth>
        <InputLabel id="launch-activation-intent-goal-label">{INTENT_POPOVER_COPY.goalLabel}</InputLabel>
        <Select
          labelId="launch-activation-intent-goal-label"
          label={INTENT_POPOVER_COPY.goalLabel}
          value={goal}
          disabled={saving}
          data-testid={T.intentGoalSelect}
          onChange={(event) => setGoal(event.target.value as IntentGoal)}
        >
          {INTENT_GOALS.map((value) => (
            <MenuItem key={value} value={value}>{capitalize(GOAL_LABELS[value])}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 0.5 }}>
          {INTENT_POPOVER_COPY.channelsLabel}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {INTENT_CHANNELS.map((id) => {
            const selected = channels.includes(id);
            return (
              <Chip
                key={id}
                size="small"
                data-testid={T.intentChannel(id)}
                label={CHANNEL_LABELS[id]}
                variant={selected ? 'filled' : 'outlined'}
                color={selected ? 'primary' : 'default'}
                aria-pressed={selected}
                disabled={saving}
                onClick={() => setChannels((prev) => toggle(prev, id))}
                sx={CHIP_FOCUS_SX}
              />
            );
          })}
        </Box>
      </Box>

      <Box>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 0.5 }}>
          {INTENT_POPOVER_COPY.productAnalyticsLabel}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {INTENT_PRODUCT_ANALYTICS.map((id) => {
            const selected = productAnalytics.includes(id);
            return (
              <Chip
                key={id}
                size="small"
                data-testid={T.intentAnalytics(id)}
                label={PRODUCT_ANALYTICS_LABELS[id]}
                variant={selected ? 'filled' : 'outlined'}
                color={selected ? 'primary' : 'default'}
                aria-pressed={selected}
                disabled={saving}
                onClick={() => setProductAnalytics((prev) => toggle(prev, id))}
                sx={CHIP_FOCUS_SX}
              />
            );
          })}
        </Box>
      </Box>

      {error && <Alert severity="error" data-testid={T.intentError}>{error}</Alert>}

      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Button
          data-testid={T.intentApply}
          variant="contained"
          size="small"
          disabled={saving}
          onClick={() => { void apply(); }}
        >
          {INTENT_POPOVER_COPY.apply}
        </Button>
        {questionnaireHref && (
          <Link
            data-testid={T.intentQuestionnaire}
            href={questionnaireHref}
            target="_blank"
            rel="noopener"
            underline="hover"
            variant="body2"
          >
            {INTENT_POPOVER_COPY.questionnaire}
          </Link>
        )}
      </Stack>
    </Stack>
  );
};

export const IntentPopover: React.FC<IntentPopoverProps> = ({ anchorEl, open, onClose, ...body }) => (
  <Popover
    open={open}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
  >
    <IntentPopoverBody onClose={onClose} {...body} />
  </Popover>
);
